import { Cancel, Confirmed } from '@hqo/react-components-library/dist/icons';
import { mediaMaxWidth } from '@hqo/react-components-library/dist/device';
import { PaymentDetails } from '@hqo/react-components-library/dist/payment/payment-details';
/*
 * ⛔ THE LIBRARY'S OWN PARTS, IMPORTED AS SELECTORS AND NOTHING ELSE.
 *
 * `PaymentDetails` renders a title, an item row and a price block, none of which take a className
 * -- so the only other way to reach them from out here is `> div:first-child > div:first-child`,
 * which silently re-targets the day the library adds a wrapper. A styled-component interpolates to
 * its own generated class, so these name the exact elements and break loudly (at build time) if
 * they are ever renamed. There is one styled-components instance in this tree (5.3.9, deduped), so
 * the class the library generates is the class this app queries.
 */
import {
  PriceContainer as PaymentDetailsPrices,
  StyledItemQuantityBlock as PaymentDetailsItemRow,
  Title as PaymentDetailsTitle,
} from '@hqo/react-components-library/dist/payment/payment-details/styles';
import { Button } from '@hqo/react-components-library/dist/atoms/button';
import { DIMENSIONS } from '@hqo/react-components-library/dist/utils';
import styled, { css } from 'styled-components';
import { skeletonAnimation } from 'components/skeleton/styles';
import { StyledResourceRowCard } from 'components/resources-list-item/styles';
import { hexToRgb } from '@hqo/react-components-library/dist/utils/hexToRgb';
import { PAYMENT_CONTENT_WIDTH } from 'shared/consts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TemperatureVote } from 'components/temperature-vote';

export const OverviewContainer = styled.div<{ isSwipeModalContent: boolean }>`
  display: grid;
  grid-gap: 8px;
  padding: ${({ isSwipeModalContent }) => (isSwipeModalContent ? 0 : '0 16px')};
`;

export const Subtitle = styled.div.attrs({ className: 'rb-rcpt-body' })`
  font: 400 16px/19.2px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const TitleContainer = styled.div`
  width: auto;
  display: flex;
  justify-content: space-between;
  align-items: stretch;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    margin-top: 25px;
  }
`;

/*
 * RES-1367: `$compact` is the booking-details CARD's usage -- see `compactPaymentBlock` below for
 * what it means and why the 12px gutter is wrong there.
 *
 * (The `addSidePadding` flag this used to carry is gone with it. It existed so the routed page
 * could put back a 16px inset that its own wrapper cancelled out; that wrapper no longer wraps the
 * payment block, so by RES-1367 nothing passed the flag and it only read as a live option.)
 */
export const PaymentMethodContainer = styled.div<{ $compact?: boolean }>`
  box-sizing: border-box;

  ${({ $compact }) =>
    $compact
      ? css`
          padding: 0;

          /* The block's own label, on the same small grey ramp as the title above it. */
          ${PaymentMethodTitle} {
            font: 600 13px/1 ${({ theme }) => theme.fonts.join()};
            color: ${({ theme }) => theme.colors.palette.systemGreyDark};
            margin-bottom: 8px;
          }
        `
      : css`
          @media only screen and (min-width: ${DIMENSIONS.LARGE}) {
            padding: 0 12px;
          }
        `}
`;

/*
 * ⛔ THE RECEIPT'S CONTENT COLUMN, AND IT IS NOT CONSTANT. `SummaryContainer` runs edge to edge and
 * `ServiceDetails` is what actually insets every section inside it — but only off the swipe modal,
 * which draws its own edge spacing and so wants none here.
 *
 * ⚠️ ANYTHING RENDERING AS A SIBLING OF THE SUMMARY MUST ASK THIS, not hard-code 16px. RES-1316 put
 * the ordered add-ons beside the summary rather than inside it, took the inset unconditionally, and
 * so indented the add-ons card on the swipe receipt while the details above it sat flush — caught in
 * review on #1940. One definition, so the two layouts cannot drift apart again.
 */
export const summarySideMargin = (isSwipeModalContent?: boolean): string => (isSwipeModalContent ? '0' : '0 16px');

// The clearance the fixed/sticky action bar needs below the pane's content. It belongs to whatever
// is LAST in the pane, which is not always the summary — see ActionBarClearance.
const ACTION_BAR_CLEARANCE = '130px';

export const SummaryContainer = styled.div.attrs({ className: 'rb-rcpt-summary' })<{ removeBottomSpacing?: boolean }>`
  display: grid;
  grid-gap: 18px;
  box-sizing: border-box;
  // The clearance exists for the fixed/sticky action bar the swipe modal and plain receipt
  // layouts render below this content — RES-999's card-embedded usage (see
  // pages/booking-details/booking-details.tsx) has no such bar, so that gap would just be dead
  // space at the bottom of the card.
  padding-bottom: ${({ removeBottomSpacing }) => (removeBottomSpacing ? 0 : ACTION_BAR_CLEARANCE)};
  overflow: hidden;
`;

/*
 * RES-1316. The ordered add-ons render BELOW the summary, so the summary is no longer the last thing
 * in the pane and its clearance no longer protects anything — the add-ons sat in the gap and the Done
 * bar covered them, cutting the answers off mid-list. The summary gives the clearance up and this
 * takes it, so whatever is last is still clear of the bar.
 *
 * ⚠️ AND ONLY WHEN THERE IS A BAR. The activity-centre pane renders no Done control (VIS-2032), so
 * there is nothing to clear and the padding is just a dead band under the last row — which is what
 * it looked like once that pane stopped drawing the button.
 *
 * ⛔ IT ALSO CARRIES THE SUMMARY'S SIDE INSET, because being a SIBLING of the summary it inherits
 * none of it. Without that, the add-ons card overhung every row above it by 16px on each side and
 * its text sat 2px adrift.
 *
 * ⚠️ AND THE INSET IS CONDITIONAL, SO THIS MUST BE TOO. `ServiceDetails` drops it on the swipe
 * modal, which supplies its own edge spacing — taking 16px unconditionally lined the card up on the
 * two-column receipt and knocked it out of line on the swipe one. Both read the same
 * `summarySideMargin`, and the flag is threaded from the identical prop the caller already hands
 * `ReceiptSummary`, so the two cannot answer differently. Matched as a MARGIN rather than padding
 * so there is still one column definition and the card's own border keeps marking its edge.
 */
export const ActionBarClearance = styled.div<{ hasActionBar?: boolean; isSwipeModalContent?: boolean }>`
  /* Joins whatever content column the summary is using — see above. */
  margin: ${({ isSwipeModalContent }) => summarySideMargin(isSwipeModalContent)};
  padding-bottom: ${({ hasActionBar = true }) => (hasActionBar ? ACTION_BAR_CLEARANCE : '0')};
`;

export const PaymentMethodTitle = styled.div.attrs({ className: 'rb-rcpt-body' })`
  font: 500 16px/24px ${({ theme }) => theme.fonts.join()};
  margin-bottom: 23px;
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const CustomIconConfirmed = styled(Confirmed)<{ isCancelledIcon: boolean }>`
  display: ${({ isCancelledIcon }) => (isCancelledIcon ? 'none' : 'flex')};
  color: #04623e;
`;

export const CustomIconCancel = styled(Cancel)<{ isCancelledIcon: boolean }>`
  display: ${({ isCancelledIcon }) => (isCancelledIcon ? 'flex' : 'none')};
  color: ${({ theme }) => theme.colors.admin.pink};
`;

export const ServiceDetails = styled.div<{ isSwipeModalContent: boolean }>`
  margin: ${({ isSwipeModalContent }) => summarySideMargin(isSwipeModalContent)};

  button[data-testid='icon-block-action'] {
    color: ${({ theme }) => theme.colors.$primary};
  }

  .icon-block-link {
    color: ${({ theme }) => theme.colors.$primary};
  }
`;

export const IconBlockContainer = styled.div`
  margin: 16px 0;
  display: grid;
  grid-gap: 16px;

  .icon-block-title {
    font-weight: bold;
  }

  & > div:last-child {
    margin-top: 12px;
  }
`;

export const StyledSummarySubtitle = styled.div.attrs({ className: 'rb-rcpt-subhead' })`
  font: 700 20px/23px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

// RES-999: swapped in for StyledSummarySubtitle only on the routed booking-details page (see
// ReceiptSummaryProps.useCompactSubtitle), so this section's heading matches the small
// grey/uppercase-weight label style of the card's other rows (When/Location/Reserved by) instead
// of standing out as its own large bold heading. Every other caller keeps StyledSummarySubtitle.
export const StyledSummarySubtitleCompact = styled.div`
  font: 600 13px/1 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const StatusTitleWrapper = styled.div.attrs({ className: 'rb-rcpt-hero-wrap' })<{ isCancelledIcon: boolean }>`
  flex-direction: row-reverse;
  gap: 16px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  align-items: center;

  ${mediaMaxWidth(css`
    margin-bottom: 16px;
  `)}
`;

export const StyledStatusIcon = styled.div<{ isCancelledIcon: boolean }>`
  background-color: ${({ theme, isCancelledIcon }): string =>
    isCancelledIcon ? 'transparent' : theme.colors.admin.green};
  opacity: 0.2;
  border-radius: 50%;
  position: absolute;
  width: 100%;
  height: 100%;
`;

export const StatusTitle = styled.div.attrs({ className: 'rb-rcpt-hero' })`
  font: 700 24px/24px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }): string => theme.colors.fontColor};
  margin-right: 8px;
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 6px;
`;

/*
 * RES-1367: the payment block as it reads inside the booking-details CARD, standing in a column
 * beside Reserved by / When / Location, rather than running down a receipt pane of its own.
 *
 * ⛔ FLUSH, because the 12px gutter below belongs to the PANE, not to this block. In a column it
 * is the only thing setting the content's left edge, so it pushed every price 12px right of the
 * card's other rows and 12px short of the card's right edge -- visibly out of line with the column
 * beside it.
 *
 * ⛔ AND TIGHTER, because the library's rhythm is a page's, not a column's: a 24px drop under the
 * title and 20px above and below the prices, inside a card whose own rows sit 4-16px apart. The
 * heading drops to the card's small grey label ramp for the same reason -- as a 20px bold line it
 * announced itself as a module header while sitting two thirds of the way INSIDE one.
 */
const compactPaymentBlock = css`
  padding: 0;

  ${PaymentDetailsTitle} {
    font: 600 13px/1 ${({ theme }) => theme.fonts.join()};
    color: ${({ theme }) => theme.colors.palette.systemGreyDark};
    margin-bottom: 8px;
  }

  /* The item row's own trailing gap, on top of the prices' lead-in below, read as a dropped line. */
  ${PaymentDetailsItemRow} {
    margin-bottom: 0;
  }

  ${PaymentDetailsPrices} {
    padding: 12px 0;
    gap: 8px;
  }
`;

export const StyledPaymentDetails = styled(PaymentDetails)<{ $compact?: boolean }>`
  margin-top: 0;
  box-sizing: border-box;

  ${({ $compact }) =>
    $compact
      ? compactPaymentBlock
      : css`
          @media only screen and (min-width: ${DIMENSIONS.LARGE}) {
            padding: 0 12px;
          }
        `}
`;

export const PaymentDetailsItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin: 2px 0 0 7px;
`;

export const CheckInContainer = styled.div`
  display: grid;
  grid-gap: 16px;
  padding: 16px;
  margin: 32px 16px 16px 16px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.greys.adminGreyMedium};
  border-radius: 8px;
`;

export const CheckInTitle = styled.div.attrs({ className: 'rb-rcpt-subhead' })`
  font: 500 20px/22px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }): string => theme.colors.fontColor};
  text-transform: capitalize;
`;

export const CheckInDescription = styled.div`
  font: 400 14px/21px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

export const CheckInButtonContainer = styled.div`
  display: flex;
  justify-self: end;
  width: 35%;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    justify-self: center;
    width: 100%;
  }
`;

export const CheckInButton = styled(Button)`
  width: 100%;
  height: 48px;
  padding: 0;
`;

// Same 16px horizontal inset as CheckInContainer above, so the vote box (BUILD-1600) aligns with
// the check-in card it sits under in both receipt panes; the component brings its own top margin.
export const ReceiptTemperatureVote = styled(TemperatureVote)`
  margin-left: 16px;
  margin-right: 16px;
`;

export const CheckedInContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  font: 400 16px/22px ${({ theme }) => theme.fonts.join()};
  font-style: italic;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};

  svg path {
    fill: ${({ theme }) => theme.colors.greys.adminGreyDark};
  }
`;

export const CheckedInIcon = styled(Confirmed)`
  margin-right: 4px;
`;

export const VisitorsCountContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  gap: 8px;
`;

export const Title = styled.div`
  font: bold 16px / 22px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.darkGrey7};
`;

export const StyledVisitorsIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.palette.systemBlackAlt};
`;

export const UnlockDorsLinkContainer = styled.div<{ $isSwipeModalContent: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${({ $isSwipeModalContent }) => ($isSwipeModalContent ? 0 : '0 16px')};
  max-height: 202px;
  overflow-y: auto;
  overflow-x: hidden;

  @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
    max-height: ${({ $isSwipeModalContent }) => ($isSwipeModalContent ? '202px' : '164px')};
  }
`;

export const UnlockDorsItemContainer = styled.a`
  display: flex;
  gap: 16px;
  padding: 16px;
  align-items: center;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  box-shadow: 0px 1px 2px 0px rgba(10, 13, 18, 0.05);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.greys.adminGreyLightAlt};
  }
`;

export const UnlockDorsInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

export const UnlockDorsTitle = styled.div`
  font: 700 16px / 19px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.darkGrey7};
`;

export const UnlockDorsDescription = styled.div`
  font: 400 14px / normal ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

export const UnlockDorsKeyIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background-color: rgba(${({ theme }) => hexToRgb(theme.colors.$primary)?.join(',')}, 0.1);
  border-radius: 8px;
`;

export const UnlockDorsChevronIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
`;

export const VisitorName = styled.div.attrs({ className: 'rb-rcpt-body' })`
  font: 400 16px /16px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

export const VisitorEmail = styled.div`
  font: 500 14px / 17px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

export const VisitorNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: 22px;
`;

export const VisitorNameWithPillContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const VisitorCountContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

/*
 * RES-1326 — the add-ons prompt is the same kind of block as the check-in card above: a bordered
 * in-pane call to action, in the same 16px horizontal band both receipt panes use. Extending rather
 * than copying is what keeps the three blocks aligned when that band changes — the alignment the note
 * on `ReceiptTemperatureVote` exists to protect.
 */
export const AddOnsPromptContainer = styled(CheckInContainer)`
  /*
   * CheckInContainer carries its own 16px horizontal margin, which inset this card 16px further than
   * the booking tile above it. The confirmation screen owns the gutter; children contribute vertical
   * rhythm only.
   */
  margin: 12px 0 0;
`;

/*
 * ⛔ `text-transform` reset deliberately. `CheckInTitle` capitalises, which is fine for the one string
 * it was written for and wrong here: it would render "Add-Ons Available", and it mangles differently in
 * every locale that does not capitalise words the way English does.
 */
export const AddOnsPromptTitle = styled(CheckInTitle)`
  text-transform: none;
`;

export const AddOnsPromptDescription = styled(CheckInDescription)``;

/*
 * ⛔ WIDTH AND HEIGHT OVERRIDDEN, and the inherited values are the reason rather than taste.
 * `CheckInButtonContainer` is a fixed 35% and `CheckInButton` is `height: 48px; padding: 0` — sized for
 * "Check in" (8 chars) and fine for "Browse add-ons" (14). It is not fine for this key's real values:
 * fr-FR / fr-CA "Voir les modules complémentaires" is 32 chars, it-IT 28, hu-HU 24. With zero padding
 * and a fixed height the label clips or runs edge to edge on desktop. `min-width` keeps the check-in
 * card's alignment as the floor while letting a long label take the room it needs; `min-height` lets it
 * wrap rather than clip.
 *
 * ⛔⛔ THE ≤MEDIUM BRANCH IS RESTATED HERE AND IT IS NOT REDUNDANT. `CheckInButtonContainer` already
 * goes to `width: 100%` below the breakpoint, but an extending `styled()` emits its rules AFTER the
 * base's at EQUAL specificity — so a bare `width: auto` at base level beats the base's own media rule
 * and shrinks the button on mobile, where it should be a full-width tap target. Read out of the emitted
 * stylesheet, not reasoned about:
 *
 *   .base { width: 35% }
 *   @media (max-width:769px) { .base { width: 100% } }
 *   .ext  { width: auto }            <- last, same specificity, wins at EVERY width
 *
 * `add-ons-prompt.spec.tsx` asserts that order directly.
 */
export const AddOnsPromptButtonContainer = styled(CheckInButtonContainer)`
  width: auto;
  min-width: 35%;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    width: 100%;
  }
`;

export const AddOnsPromptButton = styled(CheckInButton)`
  height: auto;
  min-height: 48px;
  padding: 0 16px;
`;

/*
 * RES-1326 — the booking summary tile on the post-booking confirmation.
 *
 * ⭐ EXTENDED FROM THE RESOURCE LIST'S ROW, not redrawn. That row is the shipped treatment for
 * "a thing, with a picture, that you can tap into", and the confirmation's tile is the same object in
 * a different place. Two declarations have to go, and both are list-specific rather than cosmetic:
 * `padding-right: 40px` reserves space for a favourite star this tile does not have, and the
 * `flex: 1 1 <basis>` is how a wrapping grid shares a line — here there is exactly one.
 */
export const BookingSummaryCard = styled(StyledResourceRowCard)`
  padding-right: 10px;
  flex: none;
  width: 100%;
`;

/* The pill cluster sits under the subtitle rather than beside the name; one line, not a wrap. */
export const BookingSummaryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  min-width: 0;
`;

export const BookingSummaryWhen = styled.span`
  font: 400 13px/18px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* RES-1326 — the post-booking confirmation screen's own layout. */
export const ConfirmedScreen = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: 16px;
`;

/* Scrolls, so a long add-ons card never pushes the footer out of the box. */
export const ConfirmedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const ConfirmedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ConfirmedIcon = styled(Confirmed)`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

export const ConfirmedHeading = styled.h1`
  margin: 0;
  font: 600 22px/28px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

/* Never scrolls away: the tenant must always be able to leave. */
export const ConfirmedFooter = styled.div`
  flex: 0 0 auto;
  padding-top: 16px;
`;

/*
 * RES-1326 — the brief hold while the add-on read decides which confirmation to show.
 *
 * ⛔ SHAPED LIKE THE SCREEN IT IS WAITING FOR, not a centred spinner. The modal is sized by its
 * content, so a spinner-height box jumped to the confirmation's height a beat later — a visible
 * resize under someone who has just paid. Mirroring the layout holds the box still instead, which is
 * also why these are plain blocks and not a shared skeleton component: they have to match
 * ConfirmedScreen's children, and they move when it does.
 */
export const DecidingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
`;

/* The heading row: the icon's 24px square beside a short bar for the title. */
export const DecidingHeading = styled.div`
  height: 28px;
  width: 220px;
  ${skeletonAnimation};
`;

/* Stands in for BookingSummaryTile. */
export const DecidingTile = styled.div`
  height: 84px;
  width: 100%;
  ${skeletonAnimation};
`;

/* Stands in for AddOnsPrompt, the taller of the two cards. */
export const DecidingCard = styled.div`
  height: 148px;
  width: 100%;
  ${skeletonAnimation};
`;

/* Stands in for the Done button, which the real screen keeps pinned to the bottom. */
export const DecidingFooter = styled.div`
  height: 48px;
  width: 160px;
  margin-top: 16px;
  ${skeletonAnimation};
`;

/*
 * Named for a screen reader, absent for everyone else.
 *
 * NOT `display: none` or `visibility: hidden` -- both remove it from the accessibility tree too, which
 * is the one thing it exists for. This is the standard clip-rect idiom. The repo had no helper for it;
 * its other hidden-name case puts an aria-label on a visible role="img" instead, which does not apply
 * to a description.
 */
export const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
